/** @jsxImportSource preact */
import * as m from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import type { WidgetTokens } from "../theme";

/** 分组的状态，后端给状态，挂件按状态挑圆点：待发是虚线点，取消是出错色点，其余是实心点。 */
export type WidgetOrderIcon =
  "canceled" | "delivered" | "mail" | "package" | "preparing" | "shipped";

export interface WidgetOrderItem {
  /** 规格和数量，如 `White / US 9 · Qty 1`。 */
  detail: string;
  /** 没图用占位块。 */
  imageUrl?: string;
  price: string;
  title: string;
}

/** 一个可信追踪号的展示，不代表物理包裹数量。 */
export interface WidgetOrderTracking {
  /** 承运商；没有时写 Carrier not provided，单号写 —。 */
  carrier: string;
  /** 预计送达或送达时间的整句原文。 */
  estimate: string;
  latest: string;
  /** 分包裹时替代承运商作标题。 */
  heading?: string;
  number: string;
  updatedAt?: string;
  url?: string;
}

/** 已发和待发分开：每组一个状态、若干商品，可带物流块或一句说明。 */
export interface WidgetOrderGroup {
  icon: WidgetOrderIcon;
  items: WidgetOrderItem[];
  label: string;
  /** 待发商品的预售或补货说明。 */
  note?: string;
  trackings?: WidgetOrderTracking[];
}

export interface WidgetOrderAdjustmentLine {
  /** 补充一行小字，如退款原因或时间。 */
  detail?: string;
  label: string;
  value: string;
}

/** 取消与退款，有才显示。 */
export interface WidgetOrderAdjustments {
  lines: WidgetOrderAdjustmentLine[];
  title: string;
}

export interface WidgetOrderCard {
  adjustments?: WidgetOrderAdjustments;
  groups: WidgetOrderGroup[];
  number: string;
  /** 订单页链接；没有就不出 View order。 */
  orderUrl?: string;
  payment: string;
  /** 下单日期，如 `Sep 12, 2026`，原文由后端给。 */
  placed: string;
  /** 只到城市和州。 */
  shipTo?: string;
  /** 状态胶囊：订单状态原文；取消用 `danger` 出错色，其余是墨黑实心。 */
  status: { label: string; tone?: "danger" };
  /** 订单金额，如 `USD 259.00`，原文由后端给。 */
  total: string;
  /** 追踪链接；没有就不出 Track shipment。 */
  trackUrl?: string;
}

export interface OrderCardProps {
  card: WidgetOrderCard;
  showProgress?: boolean;
  actions?: ("contact" | "not_received")[];
  /** 内容区最高多少像素，超出在卡内滚动；默认 520。给足够大的值就是完整展开。 */
  detailsMaxHeight?: number;
  onContactStore?: () => void;
  onNotReceived?: () => void;
  tokens: WidgetTokens;
}

/** 内容区默认最高这么多，超出在卡内滚动；上面的订单号和下面的合计、按钮固定。 */
const DETAILS_MAX_HEIGHT = 520;

const ORDER_CARD_STYLES = `
.gn-ticket { --gn-notch:10px; --gn-notch-tl:radial-gradient(circle at 0 0, #0000 var(--gn-notch), #000 calc(var(--gn-notch) + .5px)); --gn-notch-tr:radial-gradient(circle at 100% 0, #0000 var(--gn-notch), #000 calc(var(--gn-notch) + .5px)); --gn-notch-bl:radial-gradient(circle at 0 100%, #0000 var(--gn-notch), #000 calc(var(--gn-notch) + .5px)); --gn-notch-br:radial-gradient(circle at 100% 100%, #0000 var(--gn-notch), #000 calc(var(--gn-notch) + .5px)); box-sizing:border-box; width:100%; max-width:320px; color:var(--gn-form-ink); font-size:13px; line-height:1.45; text-align:left; }
.gn-ticket-seg { position:relative; box-sizing:border-box; background:var(--gn-form-tray); -webkit-mask-composite:source-in; mask-composite:intersect; }
.gn-ticket-seg[data-pos="first"] { border-radius:var(--gn-form-r-card) var(--gn-form-r-card) 0 0; -webkit-mask-image:var(--gn-notch-bl), var(--gn-notch-br); mask-image:var(--gn-notch-bl), var(--gn-notch-br); }
.gn-ticket-seg[data-pos="middle"] { -webkit-mask-image:var(--gn-notch-tl), var(--gn-notch-tr), var(--gn-notch-bl), var(--gn-notch-br); mask-image:var(--gn-notch-tl), var(--gn-notch-tr), var(--gn-notch-bl), var(--gn-notch-br); }
.gn-ticket-seg[data-pos="last"] { border-radius:0 0 var(--gn-form-r-card) var(--gn-form-r-card); -webkit-mask-image:var(--gn-notch-tl), var(--gn-notch-tr); mask-image:var(--gn-notch-tl), var(--gn-notch-tr); }
.gn-ticket-tear { position:absolute; top:0; left:var(--gn-notch); right:var(--gn-notch); border-top:1.5px dashed var(--gn-form-dash); pointer-events:none; }
.gn-ticket-head { display:flex; justify-content:space-between; align-items:flex-start; gap:10px; padding:14px 16px; }
.gn-ticket-stub { display:flex; flex-direction:column; gap:10px; padding:14px 12px 12px; }
.gn-order-id { display:flex; flex-direction:column; gap:2px; min-width:0; }
.gn-order-number { font-size:22px; font-weight:800; letter-spacing:-.02em; line-height:1; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
.gn-order-placed { margin-top:4px; font-size:12px; color:var(--gn-form-quiet); }
.gn-order-status { display:flex; flex-direction:column; align-items:flex-end; gap:2px; min-width:0; max-width:56%; text-align:right; }
.gn-order-status-value { display:flex; align-items:center; justify-content:flex-end; gap:6px; min-height:22px; font-size:15px; font-weight:800; letter-spacing:-.01em; line-height:1.2; overflow-wrap:anywhere; }
.gn-order-status[data-tone="danger"] .gn-order-status-value { color:var(--gn-form-danger); }
.gn-order-scroll { display:flex; flex-direction:column; gap:10px; box-sizing:border-box; padding:12px; }
.gn-order-scroll:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:-8px; border-radius:var(--gn-form-r-well); }
.gn-order-steps { display:flex; gap:6px; margin:0; padding:0; list-style:none; }
.gn-order-step { flex:1 1 0; min-width:0; box-sizing:border-box; min-height:64px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; padding:8px 4px; border-radius:var(--gn-form-r-well); background:var(--gn-form-chip); color:var(--gn-form-ink); text-align:center; }
.gn-order-step[data-current] { background:var(--gn-form-answer); color:var(--gn-form-answer-fg); }
.gn-order-step[data-future] { background:transparent; border:1.5px dashed var(--gn-form-dash); color:var(--gn-form-quiet); }
.gn-order-day { font-size:12px; font-weight:800; line-height:1.2; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
.gn-order-step-label { font-size:10px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; opacity:.85; }
.gn-order-tracks { display:flex; flex-direction:column; gap:8px; padding:0 4px; }
.gn-order-track { display:flex; flex-direction:column; gap:2px; font-size:12px; color:var(--gn-form-quiet); }
.gn-order-track-main { display:flex; align-items:flex-start; gap:6px; }
.gn-order-track-main .gn-dot { margin-top:5px; }
.gn-order-carrier { font-weight:700; color:var(--gn-form-ink); }
.gn-order-track-sub { display:flex; flex-direction:column; gap:1px; padding-left:12px; }
.gn-order-track-number { font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:11px; font-variant-numeric:tabular-nums; }
.gn-order-eta { font-weight:700; color:var(--gn-form-ink); }
.gn-order-track-sub > .gn-btn { align-self:flex-start; margin:-2px 0 -8px -12px; color:var(--gn-form-ink); text-decoration:underline; text-decoration-thickness:1px; text-underline-offset:3px; }
.gn-order-group { display:flex; flex-direction:column; gap:10px; padding:0 2px; }
.gn-order-group-head { display:flex; flex-wrap:wrap; align-items:center; gap:2px 6px; padding:0 4px; }
.gn-ticket .gn-dot[data-danger] { background:var(--gn-form-danger); }
.gn-order-note { font-size:12px; color:var(--gn-form-faint); }
.gn-order-item { display:flex; align-items:center; gap:10px; }
.gn-order-thumb { display:block; flex:none; box-sizing:border-box; width:44px; height:44px; border-radius:var(--gn-form-r-pill); background:var(--gn-form-sheet); object-fit:cover; }
.gn-order-item-text { flex:1 1 0; min-width:0; display:flex; flex-direction:column; gap:2px; }
.gn-order-item-title { font-size:13px; font-weight:700; }
.gn-order-item-detail { font-size:12px; color:var(--gn-form-quiet); }
.gn-order-price { flex:none; font-size:13px; font-weight:700; font-variant-numeric:tabular-nums; white-space:nowrap; }
.gn-order-adjust { display:flex; flex-direction:column; gap:2px; }
.gn-order-adjust-row { display:flex; justify-content:space-between; align-items:baseline; gap:8px; font-size:13px; }
.gn-order-adjust-value { font-weight:700; white-space:nowrap; }
.gn-order-adjust-detail { font-size:12px; color:var(--gn-form-quiet); }
.gn-order-summary { margin:0; gap:8px; }
.gn-order-fact { display:flex; justify-content:space-between; align-items:baseline; gap:12px; margin:0; }
.gn-order-fact > dt { flex:none; }
.gn-order-fact > dd { margin:0; min-width:0; font-size:13px; font-weight:700; text-align:right; overflow-wrap:anywhere; }
.gn-order-fact[data-total] > dd { font-size:15px; font-weight:800; letter-spacing:-.02em; font-variant-numeric:tabular-nums; }
.gn-order-actions { display:flex; gap:8px; }
.gn-order-actions > .gn-btn { flex:1 1 0; min-width:max-content; }
.gn-order-more { display:flex; flex-wrap:wrap; justify-content:center; gap:4px; }
.gn-order-bone { display:block; flex:none; background:var(--gn-form-chip); border-radius:var(--gn-form-r-pill); }
.gn-order-skel-steps { display:flex; gap:6px; }
.gn-order-skel-steps .gn-order-bone { flex:1 1 0; height:64px; border-radius:var(--gn-form-r-well); background:var(--gn-form-tray); }
.gn-order-skel-lines { display:flex; flex-direction:column; gap:6px; flex:1 1 0; min-width:0; }
.gn-order-skel-facts { display:flex; flex-direction:column; gap:10px; }
`;

type StepState = "current" | "future" | "past";

interface OrderStep {
  /** 日历格上的日期；数据里没有就不给，格里画破折号，不编日期。 */
  day?: string;
  label: string;
  state: StepState;
}

/**
 * 订单进度：下单、发货、送达三格。状态只看分组（取消的分组不算）：
 * 没有已发的分组 → 当前在「下单」；有已发的 → 当前在「发货」；分组全是已送达 → 当前在「送达」。
 * 整单取消（出错色的状态）或没有可看的分组时没有后续，不画进度。
 */
function orderSteps(card: WidgetOrderCard): OrderStep[] | undefined {
  const live = card.groups.filter((group) => group.icon !== "canceled");
  if (card.status.tone === "danger" || live.length === 0) return undefined;
  const placed = { day: card.placed, label: m.widget_order_step_placed() };
  if (live.every((group) => group.icon === "delivered")) {
    return [
      { ...placed, state: "past" },
      { label: m.widget_order_step_shipped(), state: "past" },
      { label: m.widget_order_step_delivered(), state: "current" },
    ];
  }
  if (live.some((group) => group.icon !== "preparing")) {
    return [
      { ...placed, state: "past" },
      { label: m.widget_order_step_shipped(), state: "current" },
      { label: m.widget_order_step_arrives(), state: "future" },
    ];
  }
  return [
    { ...placed, state: "current" },
    { label: m.widget_order_step_shipped(), state: "future" },
    { label: m.widget_order_step_arrives(), state: "future" },
  ];
}

/** 日历格里的小圆点：已过是商家色，当前跟字色，还没到是虚线。 */
function StepDot({ state }: { state: StepState }) {
  return (
    <i
      aria-hidden="true"
      class="gn-dot"
      data-current={state === "current" ? "" : undefined}
      data-future={state === "future" ? "" : undefined}
      data-size="8"
    />
  );
}

function Steps({ steps }: { steps: OrderStep[] }) {
  return (
    <ol aria-label={m.widget_order_progress_label()} class="gn-order-steps">
      {steps.map((step) => (
        <li
          aria-current={step.state === "current" ? "step" : undefined}
          class="gn-order-step"
          data-current={step.state === "current" ? "" : undefined}
          data-future={step.state === "future" ? "" : undefined}
          key={step.label}
        >
          {step.day === undefined ? (
            <span aria-hidden="true" class="gn-order-day">
              —
            </span>
          ) : (
            <span class="gn-order-day">{step.day}</span>
          )}
          <StepDot state={step.state} />
          <span class="gn-order-step-label">{step.label}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * 追踪记录：承运商、来源状态与时间；无 ETA 不推断。
 * 有追踪链接时最后一行是 Track shipment 文字链接，左边和上面几行的文字对齐；
 * 按钮自带的内边距用负外边距抵掉，纸面上下留白保持一致。
 */
function Tracking({
  tracking,
  tokens,
}: {
  tracking: WidgetOrderTracking;
  tokens: WidgetTokens;
}) {
  return (
    <div class="gn-order-track">
      <div class="gn-order-track-main">
        <i aria-hidden="true" class="gn-dot" />
        <span>
          <span class="gn-order-carrier">
            {tracking.heading ?? tracking.carrier}
          </span>
          {" · "}
          <span>{tracking.latest}</span>
        </span>
      </div>
      <div class="gn-order-track-sub">
        <span class="gn-order-track-number">{tracking.number}</span>
        {tracking.updatedAt !== undefined && <span>{tracking.updatedAt}</span>}
        <span class="gn-order-eta">{tracking.estimate}</span>
        {tracking.url && (
          <FormButton
            href={tracking.url}
            tokens={tokens}
            variant="ghost"
            compact
          >
            {m.widget_order_track()}
          </FormButton>
        )}
      </div>
    </div>
  );
}

function Item({ item }: { item: WidgetOrderItem }) {
  return (
    <div class="gn-order-item">
      {item.imageUrl !== undefined && item.imageUrl !== "" ? (
        <img alt="" class="gn-order-thumb" src={item.imageUrl} />
      ) : (
        <span aria-hidden="true" class="gn-order-thumb" />
      )}
      <div class="gn-order-item-text">
        <div class="gn-order-item-title">{item.title}</div>
        <div class="gn-order-item-detail">{item.detail}</div>
      </div>
      <div class="gn-order-price">{item.price}</div>
    </div>
  );
}

/** 分组名后面的小圆点：待发是虚线点，取消是出错色点，已发、已送达、邮件送达都是实心点。 */
function GroupDot({ icon }: { icon: WidgetOrderIcon }) {
  return (
    <i
      aria-hidden="true"
      class="gn-dot"
      data-danger={icon === "canceled" ? "" : undefined}
      data-future={icon === "preparing" ? "" : undefined}
    />
  );
}

function Group({ group }: { group: WidgetOrderGroup }) {
  return (
    <div class="gn-order-group">
      <div class="gn-order-group-head">
        <span class="gn-caption">
          {group.label}
          {group.items.length > 0 ? ` · ${String(group.items.length)}` : ""}
        </span>
        {group.label && <GroupDot icon={group.icon} />}
        {group.note !== undefined && (
          <span class="gn-order-note">{group.note}</span>
        )}
      </div>
      {group.items.map((item, index) => (
        <Item item={item} key={`${String(index)}:${item.title}`} />
      ))}
    </div>
  );
}

function Adjustments({ adjustments }: { adjustments: WidgetOrderAdjustments }) {
  return (
    <div class="gn-order-group">
      <div class="gn-order-group-head">
        <span class="gn-caption">{adjustments.title}</span>
      </div>
      <div class="gn-well" data-roomy>
        {adjustments.lines.map((line, index) => (
          <div class="gn-order-adjust" key={`${String(index)}:${line.label}`}>
            <div class="gn-order-adjust-row">
              <span>{line.label}</span>
              <span class="gn-order-adjust-value">{line.value}</span>
            </div>
            {line.detail !== undefined && (
              <div class="gn-order-adjust-detail">{line.detail}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 订单结果卡（PRD 2.5），票券样式：白色大圆角票，票头、内容、票根三段，接缝处各挖一对半圆缺口、中间一道虚线撕线，
 * 整张票是一块柔阴影。票头像登机牌：左边是「Order」小标签和大号订单号，右边是「Status」小标签和状态（一个小圆点加状态原文，取消用出错色）；
 * 中间的内容区在卡内滚动：内凹井里是下单 / 发货 / 送达三个日历格（已过白底、当前墨黑、未到虚线）和物流说明，
 * 下面按分组列商品（已发实心点、待发虚线点），取消与退款单独一块。
 * 票根固定：合计、付款、收货地区放进一口井，下面是按钮：Track shipment（墨黑实心）、View order（白底胶囊），
 * 都只在有链接时出；Contact store、I haven’t received this 是无底文字。
 */
export function OrderCard({
  card,
  detailsMaxHeight = DETAILS_MAX_HEIGHT,
  onContactStore,
  onNotReceived,
  tokens: t,
  showProgress = true,
  actions = ["contact", "not_received"],
}: OrderCardProps) {
  const steps = showProgress ? orderSteps(card) : undefined;
  const trackings = card.groups.flatMap((group) => group.trackings ?? []);
  const hasTrack = card.trackUrl !== undefined;
  const hasView = card.orderUrl !== undefined;
  return (
    <div
      aria-label={m.widget_orders_order_number({ number: card.number })}
      class="gn-ticket gn-order"
      role="group"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${ORDER_CARD_STYLES}`}</style>
      <div class="gn-ticket-seg gn-ticket-head" data-pos="first">
        <div class="gn-order-id">
          <div class="gn-caption">{m.widget_order_eyebrow()}</div>
          <div class="gn-order-number">{card.number}</div>
          {steps === undefined && (
            <div class="gn-order-placed">{card.placed}</div>
          )}
        </div>
        <div class="gn-order-status" data-tone={card.status.tone}>
          <div class="gn-caption">{m.widget_order_status()}</div>
          <div class="gn-order-status-value">
            <i
              aria-hidden="true"
              class="gn-dot"
              data-danger={card.status.tone === "danger" ? "" : undefined}
              data-size="8"
            />
            {card.status.label}
          </div>
        </div>
      </div>
      <div class="gn-ticket-seg" data-pos="middle">
        <i aria-hidden="true" class="gn-ticket-tear" />
        <div
          aria-label={m.widget_order_details_label()}
          class="gn-order-scroll"
          role="group"
          style={{
            maxHeight: detailsMaxHeight,
            overflowY: "auto",
            overscrollBehavior: "contain",
            scrollbarColor: "var(--gn-form-line) transparent",
            scrollbarWidth: "thin",
          }}
          tabIndex={0}
        >
          {steps !== undefined && (
            <div class="gn-well">
              <Steps steps={steps} />
              {trackings.length > 0 && (
                <div class="gn-order-tracks">
                  {trackings.map((tracking, index) => (
                    <Tracking
                      key={`${String(index)}:${tracking.number}`}
                      tracking={tracking}
                      tokens={t}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {steps === undefined && trackings.length > 0 && (
            <div class="gn-well gn-order-tracks" data-roomy>
              {trackings.map((tracking, index) => (
                <Tracking
                  key={`${String(index)}:${tracking.number}`}
                  tracking={tracking}
                  tokens={t}
                />
              ))}
            </div>
          )}
          {card.groups.map((group, index) => (
            <Group group={group} key={`${String(index)}:${group.label}`} />
          ))}
          {card.adjustments !== undefined && (
            <Adjustments adjustments={card.adjustments} />
          )}
        </div>
      </div>
      <div class="gn-ticket-seg gn-ticket-stub" data-pos="last">
        <i aria-hidden="true" class="gn-ticket-tear" />
        <dl class="gn-well gn-order-summary" data-roomy>
          <div class="gn-order-fact" data-total>
            <dt class="gn-caption">{m.widget_order_total()}</dt>
            <dd>{card.total}</dd>
          </div>
          <div class="gn-order-fact">
            <dt class="gn-caption">{m.widget_order_payment()}</dt>
            <dd>{card.payment}</dd>
          </div>
          {card.shipTo !== undefined && (
            <div class="gn-order-fact">
              <dt class="gn-caption">{m.widget_order_ship_to()}</dt>
              <dd>{card.shipTo}</dd>
            </div>
          )}
        </dl>
        <div class="gn-card-foot">
          {(hasTrack || hasView) && (
            <div class="gn-order-actions">
              {card.trackUrl !== undefined && (
                <FormButton href={card.trackUrl} tokens={t}>
                  {m.widget_order_track()}
                </FormButton>
              )}
              {card.orderUrl !== undefined && (
                <FormButton
                  href={card.orderUrl}
                  tokens={t}
                  variant={hasTrack ? "secondary" : "primary"}
                >
                  {m.widget_order_view()}
                </FormButton>
              )}
            </div>
          )}
          <div class="gn-order-more">
            {actions.includes("contact") && (
              <FormButton
                compact
                onClick={onContactStore}
                disabled={!onContactStore}
                tokens={t}
                variant="ghost"
              >
                {m.widget_order_contact()}
              </FormButton>
            )}
            {actions.includes("not_received") && (
              <FormButton
                compact
                onClick={onNotReceived}
                disabled={!onNotReceived}
                tokens={t}
                variant="ghost"
              >
                {m.widget_order_not_received()}
              </FormButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 订单结果卡的载入态：票头、内容、票根三段和实际一样，订单号、状态、进度、商品和合计换成骨架，
 * 按钮不出。整张票对读屏是一个忙碌中的分组，骨架本身读屏忽略。
 */
export function OrderCardSkeleton({ tokens: t }: { tokens: WidgetTokens }) {
  return (
    <div
      aria-busy="true"
      aria-label={m.widget_order_loading()}
      class="gn-ticket gn-order"
      role="group"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${ORDER_CARD_STYLES}`}</style>
      <div class="gn-ticket-seg gn-ticket-head" data-pos="first">
        <div class="gn-order-id">
          <div class="gn-caption">{m.widget_order_eyebrow()}</div>
          <span
            aria-hidden="true"
            class="gn-order-bone"
            style={{ height: 20, marginTop: 2, width: 92 }}
          />
        </div>
        <div class="gn-order-status">
          <div class="gn-caption">{m.widget_order_status()}</div>
          <span
            aria-hidden="true"
            class="gn-order-bone"
            style={{ height: 14, marginTop: 4, width: 72 }}
          />
        </div>
      </div>
      <div class="gn-ticket-seg" data-pos="middle">
        <i aria-hidden="true" class="gn-ticket-tear" />
        <div aria-hidden="true" class="gn-order-scroll">
          <div class="gn-well">
            <div class="gn-order-skel-steps">
              <span class="gn-order-bone" />
              <span class="gn-order-bone" />
              <span class="gn-order-bone" />
            </div>
          </div>
          {[0, 1].map((row) => (
            <div class="gn-order-item" key={row}>
              <span
                class="gn-order-bone"
                style={{
                  borderRadius: "var(--gn-form-r-cell)",
                  height: 48,
                  width: 48,
                }}
              />
              <span class="gn-order-skel-lines">
                <span
                  class="gn-order-bone"
                  style={{ height: 10, width: "55%" }}
                />
                <span
                  class="gn-order-bone"
                  style={{ height: 8, width: "75%" }}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
      <div class="gn-ticket-seg gn-ticket-stub" data-pos="last">
        <i aria-hidden="true" class="gn-ticket-tear" />
        <div aria-hidden="true" class="gn-well gn-order-skel-facts" data-roomy>
          <span class="gn-order-bone" style={{ height: 12, width: "45%" }} />
          <span class="gn-order-bone" style={{ height: 10, width: "70%" }} />
        </div>
      </div>
    </div>
  );
}
